// @ts-nocheck
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, LayoutTemplate, Check, Image as ImageIcon, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpcClient } from "@/lib/trpc";
import { getUploadUrl } from "@/lib/utils";

interface SelectFromLayoutLibraryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectLayout: (layout: {
    name: string;
    layoutId?: string;
    sheetSize?: string;
    ups?: number;
    gripperMargin?: number;
    fileUrl?: string | null;
    notes?: string;
  }) => void;
}

export function SelectFromLayoutLibraryDialog({
  open,
  onOpenChange,
  onSelectLayout,
}: SelectFromLayoutLibraryDialogProps) {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["layout-library-select", search],
    queryFn: async () => {
      const res = await (trpcClient.technical.layouts.list.query as any)({
        search: search || undefined,
        limit: 30,
      });
      return res?.items || [];
    },
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[85vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutTemplate className="h-5 w-5 text-indigo-600" />
            Chọn Bản Bình Mẫu Từ Thư Viện Chế Bản
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Kế thừa hình ảnh bài bình và các thông số kỹ thuật (Khổ in, Ups, Nhíp) từ thư viện kỹ thuật
          </DialogDescription>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo mã bài bình, tên sản phẩm, quy cách..."
              className="pl-9 h-9 text-xs bg-white"
            />
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
              <span className="text-xs">Đang tải danh mục bài bình mẫu...</span>
            </div>
          ) : !data || data.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-xs">
              Không tìm thấy bài bình mẫu nào phù hợp.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.map((item: any) => {
                const sheetSize = item.sheetSize || (item.sheetWidth && item.sheetHeight ? `${item.sheetWidth}x${item.sheetHeight}` : "—");
                const ups = item.ups || 1;
                const imageUrl = item.fileUrl ? getUploadUrl(item.fileUrl) : null;

                return (
                  <div
                    key={item.id}
                    className="flex gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:border-indigo-400 hover:shadow-sm transition-all group cursor-pointer"
                    onClick={() => {
                      onSelectLayout({
                        name: item.name || item.productName || item.layoutId,
                        layoutId: item.id || item.layoutId,
                        sheetSize: sheetSize !== "—" ? sheetSize : undefined,
                        ups,
                        gripperMargin: typeof item.gripperMargin === "number" ? item.gripperMargin : parseFloat(item.gripperMargin) || 12,
                        fileUrl: item.fileUrl || null,
                        notes: item.notes || undefined,
                      });
                      onOpenChange(false);
                    }}
                  >
                    {/* Thumbnail */}
                    <div className="w-20 h-20 rounded-md bg-slate-100 border border-slate-200 shrink-0 overflow-hidden flex items-center justify-center relative">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <ImageIcon className="h-8 w-8 text-slate-300" />
                      )}
                      <Badge
                        variant="secondary"
                        className="absolute bottom-1 right-1 text-[9px] px-1 py-0 h-4 bg-slate-900/80 text-white font-mono"
                      >
                        {ups} con
                      </Badge>
                    </div>

                    {/* Metadata */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="font-mono text-[11px] font-bold text-indigo-700">
                            {item.layoutId || "MÃ-BB"}
                          </span>
                          {item.productName && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 h-3.5 text-slate-600 break-words whitespace-normal max-w-[120px]">
                              {item.productName}
                            </Badge>
                          )}
                        </div>
                        <h4 className="text-xs font-semibold text-slate-800 line-clamp-1 group-hover:text-indigo-600">
                          {item.name || "Bản bình chuẩn"}
                        </h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Khổ in: <span className="font-semibold text-slate-700">{sheetSize}</span> cm
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-1 border-t border-slate-100 mt-1">
                        <span className="text-[10px] text-slate-400">
                          Nhíp: {item.gripperMargin || 12}mm
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs text-indigo-600 font-semibold group-hover:bg-indigo-50"
                        >
                          <Check className="h-3 w-3 mr-1" /> Chọn
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
